/**
 * Floor Store Persistence
 * Handles localStorage persistence and data migration.
 */

import { storage } from '../../lib/utils';
import { FloorId, ProjectId } from '../../types/branded';
import type { Floor, MapData, CSVData, FloorState } from './types';

export const STORAGE_KEY = 'map-editor-floors';

/**
 * Migrate old CSV structure to new structure
 * Handles backward compatibility during migration
 */
export const migrateMapData = (mapData: any): MapData => {
  // If already migrated, return as is
  if (mapData.csv !== undefined && mapData.dxf !== undefined) {
    return mapData as MapData;
  }

  // Migrate old CSV fields to new structure
  const csv: CSVData | null =
    mapData.csvRawData || mapData.csvFileName
      ? {
          rawData: mapData.csvRawData ?? '',
          fileName: mapData.csvFileName ?? 'unknown.csv',
          parsedData: mapData.csvParsedData ?? null,
          groupedLayers: mapData.csvGroupedLayers,
          selectedLayers: mapData.csvSelectedLayers ?? [],
          uploadedAt: new Date().toISOString()
        }
      : null;

  return {
    csv,
    dxf: null,
    metadata: mapData.metadata ?? {},
    assets: mapData.assets ?? [],
    objects: mapData.objects ?? [],
    graphJson: mapData.graphJson ?? { cells: [] },
    selectedElementId: mapData.selectedElementId,
    renderScale: mapData.renderScale
  };
};

/**
 * Load initial state from localStorage
 */
export const loadInitialState = (): Pick<FloorState, 'floors' | 'currentFloor'> => {
  const stored = storage.get<{ floors: any[]; currentFloor: string | null }>(
    STORAGE_KEY,
    { floors: [], currentFloor: null }
  );

  // Migrate floor data to new structure
  const migratedFloors: Floor[] = stored.floors.map((floor: any) => ({
    ...floor,
    id: floor.id as FloorId,
    lotId: floor.lotId as ProjectId,
    mapData: floor.mapData ? migrateMapData(floor.mapData) : null
  }));

  return {
    floors: migratedFloors,
    currentFloor: stored.currentFloor as FloorId | null
  };
};

/**
 * Persist floor state to localStorage
 */
export const persistState = (floors: Floor[], currentFloor: FloorId | null): void => {
  storage.set(STORAGE_KEY, { floors, currentFloor });
};

/**
 * Initialize empty map data structure
 * graphJson is initialized with empty cells array so new floors appear in viewer immediately
 */
export const initializeMapData = (): MapData => ({
  csv: null,
  dxf: null,
  metadata: {},
  assets: [],
  objects: [],
  graphJson: { cells: [] },
});
